import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import { EntitiesSupportingMultiPin } from "@mapmycustomers/shared/types/entity";
import { MultiPin } from "@mapmycustomers/shared/types/map";
import RangeModel from "@mapmycustomers/shared/types/viewModel/internalModel/RangeModel";

import { MultiPinRecordList } from "@app/component/RecordList";
import { RootState } from "@app/store/rootReducer";
import useToggleBooleanAfterTimeout from "@app/util/hook/useToggleBooleanAfterTimeout";

import ActivityItem from "./ActivityItem";
import Sidebar from "./Sidebar";
import { fetchMultiPinRecords, hideMultiPinRecords } from "./store/actions";
import { MULTIPIN_RECORDS_PAGE_SIZE } from "./store/consts";
import {
  areMultiPinRecordsLoading,
  getMultiPin,
  getMultiPinAddress,
  getMultiPinLocationQuery,
  getMultiPinRecords,
  getMultiPinRecordsListState,
  getMultiPinRecordsListTotal,
} from "./store/selectors";

const messages = defineMessages({
  title: {
    id: "dashboard.map.multiPinDrawer",
    defaultMessage: "Multiple Records",
    description: "Multi-pin records drawer title on the Dashboard page",
  },
});

interface Props {
  loading?: boolean;
  multiPin?: MultiPin;
  multiPinAddress?: string;
  onClose: () => void;
  onFetchRecords: typeof fetchMultiPinRecords.request;
  range: RangeModel;
  records: EntitiesSupportingMultiPin[];
  recordsCount: number;
}

const MultiPinActivityListDrawer: React.FC<Props> = ({
  loading,
  multiPin,
  multiPinAddress,
  onClose,
  onFetchRecords,
  range,
  records,
  recordsCount,
}) => {
  const intl = useIntl();
  const visible = !!multiPin;

  // When component is first-shown, we force to show loading skeleton
  const [firstLoading, setFirstLoading] = useState(true);
  useEffect(() => {
    if (visible && firstLoading && !loading) {
      setFirstLoading(false);
    } else if (!visible) {
      setFirstLoading(true);
    }
  }, [firstLoading, visible, loading]);

  const showLoading = useToggleBooleanAfterTimeout(1000, false, loading, true) || firstLoading;

  if (!visible) {
    return null;
  }

  return (
    <Sidebar drawerTitle={intl.formatMessage(messages.title)} onClose={onClose} visible>
      <MultiPinRecordList
        fetchRecords={onFetchRecords}
        itemComponent={ActivityItem}
        loading={showLoading}
        multiPin={multiPin}
        multiPinAddress={multiPinAddress}
        pageSize={MULTIPIN_RECORDS_PAGE_SIZE}
        range={range}
        records={records}
        recordsCount={recordsCount}
      />
    </Sidebar>
  );
};

const mapStateToProps = (state: RootState) => {
  const { range } = getMultiPinRecordsListState(state);
  return {
    loading: areMultiPinRecordsLoading(state),
    multiPin: getMultiPin(state),
    multiPinAddress: getMultiPinAddress(state),
    query: getMultiPinLocationQuery(state),
    range,
    records: getMultiPinRecords(state),
    recordsCount: getMultiPinRecordsListTotal(state),
  };
};

const mapDispatchToProps = {
  onClose: hideMultiPinRecords,
  onFetchRecords: fetchMultiPinRecords.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(MultiPinActivityListDrawer);
